/**
 * View-mode path helpers.
 *
 * These are NOT routing/access-control rules (the router and ACL don't consult
 * them) — every path here is reachable by the user's role regardless of view
 * mode. They drive view-mode UX only: which pages belong to the Manager vs Rep
 * menu, and the toast/redirect behaviour when a user lands on a page that
 * doesn't belong to their current view. That's why they live with the layout
 * feature rather than in `use-route-type.ts`.
 */

/**
 * Pages that exist only in the Manager view: they appear only in the Manager
 * menu. A manager who opens a direct link to one while in Rep view is switched
 * into Manager view so the page renders in its proper chrome (see
 * view-mode-page-guard.tsx); switching Manager→Rep while sitting on one sends
 * them home, since the page has no place in the rep menu (see
 * view-as-menu.tsx). `/activities` is deliberately absent: it lives in both
 * menus and scopes itself to the current view (team feed vs. own feed).
 */
export const MANAGER_VIEW_ONLY_PATHS = ['/team', '/campaigns'];

export function isManagerViewOnlyPath(path: string): boolean {
    return MANAGER_VIEW_ONLY_PATHS.some((x) => path === x || path.startsWith(`${x}/`));
}

/**
 * Pages that exist only in the Rep view once the manager experience is active:
 * they live in the rep flat menu but are absent from the grouped manager menu
 * (e.g. Sales Drafts). Because the manager menu can't reach them, a
 * manager who opens a direct link to one while in Manager view is switched into
 * Rep view so the page renders in its proper chrome (see view-mode-page-guard.tsx).
 */
export const REP_VIEW_ONLY_PATHS = ['/drafts'];

export function isRepViewOnlyPath(path: string): boolean {
    return REP_VIEW_ONLY_PATHS.some((x) => path === x || path.startsWith(`${x}/`));
}
